<script setup lang="ts">
import {computed, reactive, ref, useTemplateRef, watch} from "vue";
import {useIntersectionObserver, watchDebounced} from "@vueuse/core";
import {deleteStimulus, getStimuli as getStimuliFromApi} from "@/Services/stimuli";
import {Button, Dialog, Drawer, InputText, Menu} from "@/Components/Library";
import {AlphabetList, FilterBar, PageLoading, PageTitle} from "@/Components/Global";
import StimulusItem from "./StimulusItem.vue";
import {RouterLink, useRouter} from "vue-router";
import {useConfirm} from "primevue/useconfirm";
import {useToast} from "primevue/usetoast";
import PageContainer from "@/Components/Global/PageContainer.vue";
import StimuliIndexFilters from "./StimuliIndexFilters.vue";

const router = useRouter();
const confirm = useConfirm();
const toast = useToast();
const getting = ref(false);
const state = reactive({
    error: false,
    show: {
        filters: false,
        stimulusMenu: false,
    },
    loading: {
        stimuli: false,
    },
});
const params = reactive({
    page: 0,
    filter: {
        search: null,
        type: [],
    },
});
const filters = ref({
    type: {
        document: false,
        slides: false,
        video: false,
        url: false,
        image: false,
        audio: false,
    },
    ownership: "all" as "all" | "mine",
});

const search = ref<string | null>(null);

const stimuli = ref([]);
const stimuliByLetter = computed(() => {
    const stimuliByLetter = new Map<string, any[]>();
    stimuli.value?.forEach((stimulus) => {
        const letter = stimulus.title[0].toUpperCase();
        if (!stimuliByLetter.has(letter)) {
            stimuliByLetter.set(letter, []);
        }
        stimuliByLetter.get(letter).push(stimulus);
    });
    return Array.from(stimuliByLetter.entries()).sort();
});
const getStimuli = async () => {
    try {
        state.loading.stimuli = true;
        const {data, total} = await getStimuliFromApi(params);

        if (params.page > 0) {
            stimuli.value = [...stimuli.value, ...data];
        } else {
            stimuli.value = data;
        }

        loadMore.value = data.length && stimuli.value.length < total;
    } catch (e) {
        console.error(e);
        state.error = e.response?.data?.message || e.message || "An error occurred";
    } finally {
        state.loading.stimuli = false;
    }
};
getStimuli();

const selectedStimulus = computed(() => stimuli.value.find((q) => q.id === state.show.stimulusMenu));
const showStimulusMenu = computed({
    get: () => !!state.show.stimulusMenu,
    set: (val) => (state.show.stimulusMenu = val),
});

const totalActiveFilters = computed(() => {
    let count = 0;
    if (Object.values(filters.value.type).some((f) => f)) {
        count++;
    }

    if (filters.value.ownership && filters.value.ownership !== "all") {
        count++;
    }

    return count;
});

const stimulusMenuItemsBase = [
    {
        label: "Edit",
        icon: "prime:pencil",
        command: () => {
            router.push({name: "stimuli.edit", params: {stimulus: selectedStimulus.value?.id}});
        },
    },
    {
        label: "Delete",
        icon: "prime:trash",
        command: () => {
            const currentStimulus = selectedStimulus.value;
            confirm.require({
                header: `Delete "${currentStimulus.title}"`,
                message: "Are you sure you want to delete this stimulus?",
                acceptLabel: "Yes",
                rejectLabel: "No",
                acceptClass:
                    "px-3 py-1.5 rounded-md confirm-decline-yes text-white font-bold bg-red-600 focus:ring-red-400",
                rejectClass: "px-3 py-1.5 font-bold confirm-decline-no",
                accept: async () => {
                    await deleteStimulus(currentStimulus.id);
                    toast.add({
                        severity: "success",
                        summary: "Success",
                        detail: "You have deleted the stimulus.",
                        life: 3000,
                    });
                    stimuli.value = stimuli.value.filter((q) => q.id !== currentStimulus.id);
                },
            });
            state.show.stimulusMenu = null;
        },
        class: "text-red-600 hover:bg-red-100",
    },
];

const stimulusMenuItems = ref([
    {
        label: "Stimulus",
        items: [],
    },
]);

const toggleStimulusMenu = (stimulus) => {
    stimulusMenuItems.value[0].items = stimulusMenuItemsBase;
    state.show.stimulusMenu = stimulus.id;
    stimulusMenuItems.value[0].label = Str.of(selectedStimulus.value?.title ?? "Stimulus").truncate().value;
};

const loadMore = ref(false);
const loadMoreIntersection = useTemplateRef("loadMoreIntersection");
const {stop} = useIntersectionObserver(
    loadMoreIntersection,
    ([{isIntersecting}], observerElement) => isIntersecting && loadMore.value && params.page++,
);

const applyFilters = () => {
    const newParams = {
        page: 0,
        filter: {
            ...params.filter,
            type: Object.keys(filters.value.type).filter((key) => filters.value.type[key]),
            ownership: filters.value.ownership,
        },
    };
    Object.assign(params, newParams);
    state.show.filters = false;
};

const clearFilters = () => {
    filters.value.type = {
        document: false,
        slides: false,
        video: false,
        url: false,
        image: false,
        audio: false,
    };
    filters.value.ownership = "all";
    applyFilters();
};

watchDebounced(
    search,
    async () => {
        loadMore.value = false;
        const newParams = {
            page: 0,
            filter: {
                ...params.filter,
                search: search.value,
            },
        };
        Object.assign(params, newParams);
    },
    {debounce: 500},
);

watch(() => params, getStimuli, {deep: true});
</script>
<template>
    <PageContainer>
        <div class="mb-2">
            <PageTitle title="Stimuli" :show-back-button="false" />
        </div>
        <div class="-sticky top-2 z-10 mb-3">
            <InputText type="search" v-model="search" placeholder="Search stimuli" fluid />
        </div>
        <div class="flex items-stretch gap-6">
            <!-- filters -->
            <div class="sticky top-2 hidden h-full w-full max-w-sm flex-none 2xl:block">
                <StimuliIndexFilters
                    v-model="filters"
                    :total-active-filters="totalActiveFilters"
                    @apply="applyFilters"
                    @clear="clearFilters"
                />
            </div>
            <!-- content -->
            <div class="grow pb-6">
                <template v-for="[letter, stimuli] in stimuliByLetter" :key="letter">
                    <AlphabetList :letter="letter" :vertical="true">
                        <div
                            class="mb-5 mt-3 grid grid-cols-2 gap-3 text-left md:grid-cols-3 2xl:grid-cols-3"
                            v-auto-animate
                        >
                            <template v-for="stimulus in stimuli" :key="stimulus.id">
                                <router-link :to="{name: 'stimuli.show', params: {stimulus: stimulus.id}}">
                                    <StimulusItem :stimulus="stimulus" has-menu @menu="toggleStimulusMenu(stimulus)" />
                                </router-link>
                            </template>
                        </div>
                    </AlphabetList>
                </template>
                <PageLoading v-if="state.loading.stimuli" class="pt-16" />
                <p v-if="state.error" class="mt-8 rounded border border-red-600 bg-red-50 p-2 text-center">
                    {{ state.error }}
                </p>
                <div
                    v-if="!stimuli?.length && !state.loading.stimuli"
                    class="flex grow flex-col items-center justify-center gap-2 p-6 sm:h-auto"
                >
                    <p class="mb-3 text-sm">
                        {{
                            search || totalActiveFilters
                                ? "No matching stimuli found."
                                : "You have not created a stimulus yet."
                        }}
                    </p>
                    <RouterLink :to="{name: 'stimuli.create'}">
                        <Button
                            class="bg-askr-green text-white hover:brightness-110"
                            :icon="{
                                icon: 'mdi:plus',
                                width: '24',
                            }"
                        >
                            Create a new stimulus
                        </Button>
                    </RouterLink>
                </div>
            </div>
        </div>
        <div v-if="!state.loading.stimuli && !state.error && loadMore">
            <span ref="loadMoreIntersection"></span>
            <div class="flex justify-center">
                <Button
                    @click="params.page++"
                    :loading="!!state.loading.stimuli"
                    :disabled="!!state.loading.stimuli || !loadMore"
                >
                    {{ loadMore || !stimuli.length ? "Load more" : "No more results" }}
                </Button>
            </div>
        </div>
        <FilterBar v-model="state.show.filters" :total-active-filters="totalActiveFilters">
            <RouterLink :to="{name: 'stimuli.create'}">
                <Button
                    :icon="{icon: 'mdi:plus', width: '36'}"
                    rounded
                    class="pointer-events-auto h-14 w-14 bg-askr-green text-white shadow-stone-600"
                />
            </RouterLink>
        </FilterBar>
        <Dialog
            v-model:visible="showStimulusMenu"
            modal
            dismissable-mask
            class="max-w-md overflow-hidden"
            :closable="false"
            :show-header="false"
        >
            <template #container>
                <Menu ref="menu" :model="stimulusMenuItems" />
            </template>
        </Dialog>
        <Drawer
            v-model:visible="state.show.filters"
            position="bottom"
            header="Filters"
            class="max-h-dvh max-w-3xl overflow-y-auto"
        >
            <div class="pb-12">
                <StimuliIndexFilters
                    v-model="filters"
                    :total-active-filters="totalActiveFilters"
                    @apply="applyFilters"
                    @clear="clearFilters"
                />
            </div>
        </Drawer>
    </PageContainer>
</template>
<style scoped></style>
